// admin.onlytrade.ai/service-controls — platform-wide feature flags. Toggle
// any sidebar surface on/off for every business at once. Hits
// /api/admin/feature-flags. Simple list grouped by category, with inline
// edit for label/description/admin_url.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { featureFlags as api, type FeatureFlag } from '../api';
import { useToast } from '../lib/toast';

const CATEGORY_LABELS: Record<string, string> = {
  core:     'Core',
  tools:    'Tools',
  ops:      'Operations',
  settings: 'Settings',
};

export default function ServiceControlsPage() {
  const { push: toast } = useToast();
  const [flags, setFlags] = useState<FeatureFlag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<FeatureFlag | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.list();
      setFlags(d.flags);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function toggle(f: FeatureFlag) {
    setBusyKey(f.feature_key);
    try {
      const r = await api.update(f.feature_key, { is_enabled: !f.is_enabled });
      setFlags(prev => (prev || []).map(x => x.feature_key === f.feature_key ? r.flag : x));
      toast(`${r.flag.label} ${r.flag.is_enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, 'error');
    } finally { setBusyKey(null); }
  }

  const grouped = useMemo(() => {
    const m = new Map<string, FeatureFlag[]>();
    for (const f of flags || []) {
      const k = f.category || 'other';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(f);
    }
    return [...m.entries()];
  }, [flags]);

  const stats = useMemo(() => {
    if (!flags) return { total: 0, on: 0, off: 0 };
    return { total: flags.length, on: flags.filter(f => f.is_enabled).length, off: flags.filter(f => !f.is_enabled).length };
  }, [flags]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-1">Service Controls</h1>
      <p className="text-ink-muted text-sm mb-5">
        Platform-wide on/off per surface. Disabling hides the sidebar item and bounces users back to <code>/dashboard</code> if they try to visit. Applies to every business — no per-tenant overrides.
      </p>

      {error && <div className="bg-red-900/30 border border-red-700 text-red-200 p-3 rounded mb-4">{error}</div>}

      <div className="flex gap-3 mb-6">
        <Stat label="Total" value={stats.total} />
        <Stat label="Enabled" value={stats.on} accent="green" />
        <Stat label="Disabled" value={stats.off} accent="red" />
      </div>

      {flags === null ? (
        <div className="text-ink-muted">Loading…</div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([cat, list]) => (
            <div key={cat}>
              <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2">
                {CATEGORY_LABELS[cat] || cat} <span className="text-ink-muted font-normal ml-1">· {list.length} feature{list.length === 1 ? '' : 's'}</span>
              </div>
              <div className="grid gap-2">
                {list.map(f => (
                  <div key={f.feature_key} className={`bg-ink-card border ${f.is_enabled ? 'border-ink-border' : 'border-red-900/50'} rounded-lg p-4 flex items-center gap-4`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center gap-2">
                        {f.label}
                        <code className="text-xs bg-ink-bg border border-ink-border px-1.5 py-0.5 rounded text-ink-muted">{f.feature_key}</code>
                      </div>
                      {f.description && <div className="text-xs text-ink-muted mt-1">{f.description}</div>}
                    </div>
                    <button onClick={() => setEditing(f)} className="px-3 py-1.5 rounded bg-ink-hover hover:bg-ink-border text-sm">Edit</button>
                    {f.admin_url
                      ? <a href={f.admin_url} className="px-3 py-1.5 rounded bg-ink-hover hover:bg-ink-border text-sm">Open editor →</a>
                      : <span className="px-3 py-1.5 text-xs text-ink-muted italic">No editor yet</span>}
                    <Toggle on={f.is_enabled} disabled={busyKey === f.feature_key} onChange={() => toggle(f)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditFlagModal flag={editing} onClose={() => setEditing(null)} onSaved={updated => {
        setFlags(prev => (prev || []).map(x => x.feature_key === updated.feature_key ? updated : x));
        setEditing(null);
        toast(`Saved ${updated.label}`);
      }} />}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'green' | 'red' }) {
  const colour =
    accent === 'green' ? 'text-emerald-400' :
    accent === 'red'   ? 'text-red-400' :
                         'text-ink-text';
  return (
    <div className="bg-ink-card border border-ink-border rounded-lg px-4 py-3">
      <div className={`text-2xl font-bold ${colour}`}>{value}</div>
      <div className="text-xs text-ink-muted uppercase">{label}</div>
    </div>
  );
}

function Toggle({ on, disabled, onChange }: { on: boolean; disabled?: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} disabled={disabled}
            className={`relative w-12 h-7 rounded-full transition-colors ${on ? 'bg-emerald-600' : 'bg-ink-border'} ${disabled ? 'opacity-50 cursor-wait' : ''}`}
            aria-pressed={on}>
      <span className={`block w-5 h-5 rounded-full bg-white shadow transform transition-transform ${on ? 'translate-x-6' : 'translate-x-1'} translate-y-1`} />
    </button>
  );
}

function EditFlagModal({ flag, onClose, onSaved }: { flag: FeatureFlag; onClose: () => void; onSaved: (f: FeatureFlag) => void }) {
  const [label, setLabel] = useState(flag.label);
  const [description, setDescription] = useState(flag.description || '');
  const [adminUrl, setAdminUrl] = useState(flag.admin_url || '');
  const [order, setOrder] = useState(flag.display_order);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      const r = await api.update(flag.feature_key, {
        label,
        description: description.trim() || null,
        admin_url:  adminUrl.trim() || null,
        display_order: order,
      });
      onSaved(r.flag);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-ink-card border border-ink-border rounded-lg p-5 w-full max-w-lg mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Edit {flag.feature_key}</h2>
          <button onClick={onClose} className="text-ink-muted hover:text-ink-text text-2xl leading-none">&times;</button>
        </div>

        {err && <div className="bg-red-900/30 border border-red-700 text-red-200 p-3 rounded mb-3 text-sm">{err}</div>}

        <Field label="Label">
          <input value={label} onChange={e => setLabel(e.target.value)}
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        </Field>
        <Field label="Description">
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
                    className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        </Field>
        <Field label="Editor URL (admin SPA path)">
          <input value={adminUrl} onChange={e => setAdminUrl(e.target.value)}
                 placeholder="/calculators"
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
          <div className="text-xs text-ink-muted mt-1">Where this feature is edited in admin. Leave blank if no editor exists yet.</div>
        </Field>
        <Field label="Display order">
          <input type="number" value={order} onChange={e => setOrder(parseInt(e.target.value, 10) || 100)}
                 className="w-full bg-ink-bg border border-ink-border rounded px-3 py-2" />
        </Field>

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded bg-ink-hover">Cancel</button>
          <button onClick={save} disabled={busy}
                  className="px-4 py-2 rounded bg-brand-600 hover:bg-brand-700 text-white font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block mb-3">
      <span className="block text-sm font-semibold mb-1">{label}</span>
      {children}
    </label>
  );
}
